// src/components/author/ManuscriptsList.jsx
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Search,
  Eye,
  X,
  ChevronLeft,
  ChevronRight,
  Grid,
  List,
  RefreshCw,
  FileText,
  Calendar,
  Clock,
  Upload,
  Users,
  Tag,
  ArrowLeft,
  ArrowRight,
  Loader,
  Hash,
  Download,
  File as FileIcon,
  FileSpreadsheet,
  Image as ImageIcon,
  FileArchive
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from '../../utils/axios';
import { showToast } from '../../utils/toast';
import RichTextViewer from '../common/RichTextViewer';
import ManuscriptActions, {
  getContinueSubtype
} from './ManuscriptActions';
import {
  formatStatus,
  formatDate,
  getStatusClass,
  getStatusIcon,
  StatusBadge,
  handleDownloadFile
} from '../../utils/utils.global';

const ManuscriptsList = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();

  // Parse URL query parameters
  const queryParams = new URLSearchParams(location.search);
  const urlManuscriptId = queryParams.get('a');
  const urlStatus = queryParams.get('status');
  const urlReturned = queryParams.get('returned');
  const urlPage = parseInt(queryParams.get('page')) || 1;
  const urlSearch = queryParams.get('search') || '';

  const [loading, setLoading] = useState(true);
  const [manuscripts, setManuscripts] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: urlPage,
    totalPages: 1,
    totalCount: 0,
    limit: 10
  });

  const [searchTerm, setSearchTerm] = useState(urlSearch);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(urlSearch);
  const [filter, setFilter] = useState(urlStatus || 'all');
  const [viewType, setViewType] = useState(() => {
    const saved = localStorage.getItem('manuscripts_view');
    return saved || 'table';
  });
  const [selectedManuscript, setSelectedManuscript] = useState(null);
  const [selectedManuscriptIndex, setSelectedManuscriptIndex] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [relatedSubmissions, setRelatedSubmissions] = useState([]);
  const [loadingRelated, setLoadingRelated] = useState(false);
  const [stats, setStats] = useState({
    submitted: 0,
    inReview: 0,
    returned: 0,
    accepted: 0,
    rejected: 0,
    draft: 0
  });

  // ---------------------------------------------------------
  // Effects
  // ---------------------------------------------------------

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch manuscripts when filters change
  useEffect(() => {
    fetchManuscripts();
  }, [pagination.currentPage, filter, debouncedSearchTerm]);

  // Handle URL parameters on mount 
  useEffect(() => {
    if (urlStatus) setFilter(urlStatus);
    if (urlSearch) setSearchTerm(urlSearch);
  }, [urlStatus, urlSearch]);

  // Auto-open a manuscript if `?a=` is present
  useEffect(() => {
    if (urlManuscriptId && manuscripts.length > 0) {
      const manuscriptIndex = manuscripts.findIndex(
        (m) => (m.revision_id || m.id) === urlManuscriptId
      );
      if (manuscriptIndex !== -1) {
        setSelectedManuscript(manuscripts[manuscriptIndex]);
        setSelectedManuscriptIndex(manuscriptIndex);
        setShowViewModal(true);
        fetchRelatedSubmissions(urlManuscriptId);
      }
    }
  }, [urlManuscriptId, manuscripts]);

  // Returned filter shortcut
  useEffect(() => {
    if (urlReturned === 'true') setFilter('returned');
  }, [urlReturned]);

  // Persist view type
  useEffect(() => {
    localStorage.setItem('manuscripts_view', viewType);
  }, [viewType]);

  // ---------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------

  const fetchManuscripts = async () => {
    setLoading(true);
    try {
      let statusFilter = filter;
      if (filter === 'returned') {
        statusFilter = 'returned_for_revision,returned_for_correction,rejected';
      }

      const response = await axios.get('/api/authors/submissions', {
        params: {
          page: pagination.currentPage,
          limit: pagination.limit,
          search: debouncedSearchTerm,
          status: statusFilter,
          sortBy: 'id',
          sortOrder: 'DESC'
        }
      });

      if (response.data.status === 'success') {
        setManuscripts(response.data.submissions);
        setPagination(response.data.pagination);
        setStats(response.data.stats);
      }
    } catch (err) {
      console.error('Error fetching manuscripts:', err);
      showToast('Failed to load manuscripts. Please refresh the page.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------
  // File list helpers
  // ---------------------------------------------------------

  // Maps each possible file key from the API to a friendly label.
  // Add/rename entries here if the backend changes field names.
  const FILE_LABELS = {
    manuscript_file: 'Manuscript',
    cover_letter_file: 'Cover Letter',
    tables: 'Tables',
    figures: 'Figures',
    supplementary_material: 'Supplementary Material',
    tracked_manuscript_file: 'Manuscript with Tracked Changes',
    graphic_abstract: 'Graphical Abstract',
    manuscriptPhoto: 'Cover Image',
    document_file: 'Document'
  };

  // Build a normalized list of { key, label, url } for a given manuscript,
  // filtering out anything falsy. Handles both camelCase (files.coverLetter)
  // and snake_case (cover_letter_file) shapes from the API.
  const getManuscriptFiles = (manuscript) => {
    if (!manuscript) return [];

    const source =
      manuscript.files && typeof manuscript.files === 'object'
        ? manuscript.files
        : manuscript;

    const seen = new Set();
    const files = [];

    const pushFile = (key, url) => {
      if (!url || typeof url !== 'string') return;
      const normalized = url.trim();
      if (!normalized || seen.has(normalized)) return;
      seen.add(normalized);
      files.push({
        key,
        label: FILE_LABELS[key] || key.replace(/_/g, ' '),
        url: normalized
      });
    };

    // Prefer the canonical order defined in FILE_LABELS
    Object.keys(FILE_LABELS).forEach((key) => {
      if (source[key]) pushFile(key, source[key]);
    });

    // Then pick up any other *file* keys we didn't explicitly list
    Object.entries(source).forEach(([key, value]) => {
      if (FILE_LABELS[key]) return; // already handled
      if (!/file|url/i.test(key)) return;
      if (typeof value !== 'string') return;
      pushFile(key, value);
    });

    return files;
  };

  // Pick an icon based on file extension
  const getFileIcon = (url) => {
    const ext = String(url).split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return ImageIcon;
    if (['xls', 'xlsx', 'csv'].includes(ext)) return FileSpreadsheet;
    if (['zip', 'rar', '7z'].includes(ext)) return FileArchive;
    if (['pdf', 'doc', 'docx'].includes(ext)) return FileText;
    return FileIcon;
  };

  // Human-friendly filename for display
  const displayFilename = (url) => {
    try {
      const name = decodeURIComponent(String(url).split('/').pop() || '');
      return name || 'file';
    } catch {
      return String(url).split('/').pop() || 'file';
    }
  };

  const fetchRelatedSubmissions = async (manuscriptId) => {
    if (!manuscriptId) return;
    setLoadingRelated(true);
    try {
      const response = await axios.get(
        `/api/authors/submissions/${manuscriptId}/related`
      );
      if (response.data.status === 'success') {
        setRelatedSubmissions(response.data.submissions || []);
      } else {
        setRelatedSubmissions([]);
      }
    } catch (err) {
      console.error('Error fetching related submissions:', err);
      setRelatedSubmissions([]);
    } finally {
      setLoadingRelated(false);
    }
  };

  // ---------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------

  const isDraftStatus = (status) =>
    ['draft', 'saved', 'drafted', 'revision_saved', 'correction_saved'].includes(status);

  const isReturnedForCorrection = (status) => status === 'returned_for_correction';
  const isReturnedForRevision = (status) => status === 'returned_for_revision';

  const buildManuscriptUrl = (id, overrides = {}) => {
    const params = new URLSearchParams();
    const page = overrides.page ?? pagination.currentPage;
    const search = overrides.search ?? searchTerm;
    const status = overrides.status ?? filter;
    const a = overrides.a;

    if (a) params.set('a', a);
    params.set('page', String(page));
    if (search) params.set('search', search);
    if (status && status !== 'all') params.set('status', status);

    return `/manuscripts?${params.toString()}`;
  };

  const handleContinueSubmission = (manuscriptId, subtype = 'new') => {
    if (subtype === 'correction') {
      navigate(`/submit?a=${manuscriptId}&status=correction&correction=true`);
    } else if (subtype === 'revision') {
      navigate(`/submit?a=${manuscriptId}&status=revision&revision=true`);
    } else {
      navigate(`/submit?a=${manuscriptId}&status=${subtype}`);
    }
  };

  // ---------------------------------------------------------
  // Navigation handlers
  // ---------------------------------------------------------

  const handleViewManuscript = (manuscript, index) => {
    setSelectedManuscript(manuscript);
    setSelectedManuscriptIndex(index);
    setShowViewModal(true);
    fetchRelatedSubmissions(manuscript.revision_id || manuscript.id);
    navigate(
      buildManuscriptUrl(null, { a: manuscript.revision_id || manuscript.id }),
      { replace: true }
    );
  };



  const handleSelectRelatedManuscript = (submission) => {
    const manuscriptIndex = manuscripts.findIndex(
      (m) => (m.revision_id || m.id) === (submission.revision_id || submission.id)
    );
    setSelectedManuscript(submission);
    setSelectedManuscriptIndex(manuscriptIndex !== -1 ? manuscriptIndex : null);
    fetchRelatedSubmissions(submission.revision_id || submission.id);
    navigate(
      buildManuscriptUrl(null, { a: submission.revision_id || submission.id }),
      { replace: true }
    );
  };

  const handleNavigateManuscript = (direction) => {
    const newIndex = selectedManuscriptIndex + direction;
    if (newIndex >= 0 && newIndex < manuscripts.length) {
      const manuscript = manuscripts[newIndex];
      setSelectedManuscript(manuscript);
      setSelectedManuscriptIndex(newIndex);
      fetchRelatedSubmissions(manuscript.revision_id || manuscript.id);
      navigate(
        buildManuscriptUrl(null, { a: manuscript.revision_id || manuscript.id }),
        { replace: true }
      );
    }
  };

  const handleCloseModal = () => {
    setShowViewModal(false);
    setSelectedManuscript(null);
    setSelectedManuscriptIndex(null);
    navigate(buildManuscriptUrl(), { replace: true });
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setPagination((prev) => ({ ...prev, currentPage: page }));
      navigate(buildManuscriptUrl(null, { page }), { replace: true });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    navigate(buildManuscriptUrl(null, { page: 1, search: value }), { replace: true });
  };

  const handleFilterChange = (e) => {
    const value = e.target.value;
    setFilter(value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    navigate(buildManuscriptUrl(null, { page: 1, status: value }), { replace: true });
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilter('all');
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    navigate('/manuscripts', { replace: true });
  };

  const handleRefresh = () => fetchManuscripts();

  // ---------------------------------------------------------
  // Render
  // ---------------------------------------------------------

  if (loading && manuscripts.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-purple-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading manuscripts...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 min-h-screen bg-gray-50">
      <div className="py-6">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">My Manuscripts</h1>
              <p className="mt-1 text-sm text-gray-500">
                Manage and track your submitted manuscripts
              </p>
            </div>
            <button
              onClick={() => (window.location.href = '/submit')}
              className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Upload className="w-4 h-4 mr-2" />
              New Submission
            </button>
          </div>

          {/* Action Bar */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  Total: <span className="font-medium">{pagination.totalCount}</span>
                </span>
                <span className="text-gray-300">|</span>
                <span className="text-sm text-gray-600">
                  Showing:{' '}
                  <span className="font-medium">
                    {(pagination.currentPage - 1) * pagination.limit + 1}-
                    {Math.min(
                      pagination.currentPage * pagination.limit,
                      pagination.totalCount
                    )}
                  </span>
                </span>
              </div>

              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setViewType('table')}
                    className={`p-2 rounded-md transition-all ${viewType === 'table'
                        ? 'bg-white shadow-sm text-purple-600'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                      }`}
                    title="Table View"
                  >
                    <List className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewType('grid')}
                    className={`p-2 rounded-md transition-all ${viewType === 'grid'
                        ? 'bg-white shadow-sm text-purple-600'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-200'
                      }`}
                    title="Grid View"
                  >
                    <Grid className="w-4 h-4" />
                  </button>
                </div>

                <button
                  onClick={handleRefresh}
                  disabled={loading}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg disabled:opacity-50"
                  title="Refresh"
                >
                  <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by title, ID, or abstract..."
                    value={searchTerm}
                    onChange={handleSearch}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div>
                <select
                  value={filter}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="all">All Status</option>
                  <option value="draft">Draft</option>
                  <option value="submitted">Submitted</option>
                  <option value="under_review">In Review</option>
                  <option value="returned_for_revision">Revision Required</option>
                  <option value="returned_for_correction">Correction Required</option>
                  <option value="accepted">Accepted</option>
                  <option value="rejected">Rejected</option>
                  <option value="returned">Returned/Rejected</option>
                </select>
              </div>

              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="px-2 py-1 bg-yellow-100 rounded">
                  In Review: {stats.inReview}
                </span>
                <span className="px-2 py-1 bg-blue-100 rounded">
                  Returned: {stats.returned}
                </span>
              </div>
            </div>

            {(searchTerm || filter !== 'all') && (
              <div className="mt-4">
                <button
                  onClick={clearFilters}
                  className="text-sm text-purple-600 hover:text-purple-800 flex items-center gap-1"
                >
                  <X className="w-4 h-4" />
                  Clear all filters
                </button>
              </div>
            )}
          </div>

          {/* Manuscripts Display */}
          {manuscripts.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 text-center py-12">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No manuscripts found
              </h3>
              <p className="text-gray-500">
                {searchTerm || filter !== 'all'
                  ? 'Try adjusting your search or filters'
                  : "You haven't submitted any manuscripts yet"}
              </p>
              {(searchTerm || filter !== 'all') && (
                <button
                  onClick={clearFilters}
                  className="mt-4 text-purple-600 hover:text-purple-800"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : viewType === 'table' ? (
            /* Table View */
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-purple-600">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                        Manuscript
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                        Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                        Submitted
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {manuscripts.map((manuscript, index) => (
                      <tr
                        key={manuscript.revision_id || manuscript.id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 max-w-xs truncate">
                          <button
                            onClick={() => handleViewManuscript(manuscript, index)}
                            className="text-[--asfi-purple] hover:text-[--asfi-blue] hover:underline font-medium max-w-xs truncate"
                            title="View Manuscript Details"
                          >
                            {manuscript.title}
                          </button>
                          <br />
                          <span className="text-sm text-gray-500">
                            {manuscript.revision_id || manuscript.id}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {manuscript.article_type}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusClass(
                              manuscript.status
                            )}`}
                          >
                            {getStatusIcon(manuscript.status)}
                            {formatStatus(manuscript.status)}
                          </span>
                          <StatusBadge status={manuscript.status} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(
                            manuscript.date_submitted || manuscript.process_start_date
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex items-center justify-start gap-2">
                            <button
                              onClick={() => handleViewManuscript(manuscript, index)}
                              className="px-2.5 py-1 text-xs font-medium text-gray-700 hover:text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                              title="View Details"
                            >
                              View Details
                            </button>

                            <ManuscriptActions
                              submission={manuscript}
                              navigate={navigate}
                              onContinue={handleContinueSubmission}
                              size="sm"
                              variant="buttons"
                              emptyFallback={null}
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            /* Grid View */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {manuscripts.map((manuscript, index) => (
                <div
                  key={manuscript.revision_id || manuscript.id}
                  className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-purple-600 mb-1">
                          <button
                            onClick={() => handleViewManuscript(manuscript, index)}
                            className="hover:text-purple-800 hover:underline"
                            title="View Manuscript Details"
                          >
                            {manuscript.revision_id || manuscript.id}
                          </button>
                        </p>
                        <h3 className="text-lg font-semibold text-gray-900 leading-tight mb-2">
                          {manuscript.title}
                        </h3>
                      </div>
                      <span
                        className={`ml-2 inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusClass(
                          manuscript.status
                        )}`}
                      >
                        {getStatusIcon(manuscript.status)}
                        {formatStatus(manuscript.status)}
                      </span>
                    </div>

                    <div className="flex items-center mb-4">
                      <Tag className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-600">
                        {manuscript.article_type}
                      </span>
                    </div>

                    {manuscript.keywords && manuscript.keywords.length > 0 && (
                      <div className="flex items-start mb-4">
                        <Hash className="w-4 h-4 text-gray-400 mr-2 mt-0.5 flex-shrink-0" />
                        <div className="flex flex-wrap gap-1">
                          {manuscript.keywords.slice(0, 3).map((keyword, i) => (
                            <span
                              key={i}
                              className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded"
                            >
                              {keyword}
                            </span>
                          ))}
                          {manuscript.keywords.length > 3 && (
                            <span className="text-xs text-gray-500">
                              +{manuscript.keywords.length - 3}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center mb-4">
                      <Calendar className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-600">
                        Submitted:{' '}
                        {formatDate(
                          manuscript.date_submitted || manuscript.process_start_date
                        )}
                      </span>
                    </div>

                    <div className="flex gap-2">
                      <button
                        onClick={() => handleViewManuscript(manuscript, index)}
                        className="flex-1 px-3 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center gap-2"
                      >
                        <Eye className="w-4 h-4" />
                        View
                      </button>

                      <ManuscriptActions
                        submission={manuscript}
                        navigate={navigate}
                        onContinue={handleContinueSubmission}
                        size="md"
                        variant="buttons"
                        emptyFallback={null}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="mt-6 bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div className="text-sm text-gray-700">
                Page {pagination.currentPage} of {pagination.totalPages}
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>
                <div className="flex gap-1">
                  {Array.from(
                    { length: Math.min(5, pagination.totalPages) },
                    (_, i) => {
                      let pageNum;
                      if (pagination.totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (pagination.currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (
                        pagination.currentPage >=
                        pagination.totalPages - 2
                      ) {
                        pageNum = pagination.totalPages - 4 + i;
                      } else {
                        pageNum = pagination.currentPage - 2 + i;
                      }

                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-8 h-8 rounded text-sm ${pagination.currentPage === pageNum
                              ? 'bg-purple-600 text-white'
                              : 'border border-gray-300 hover:bg-gray-50'
                            }`}
                        >
                          {pageNum}
                        </button>
                      );
                    }
                  )}
                </div>
                <button
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                  className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ============ View Manuscript Modal ============ */}
      {showViewModal && selectedManuscript && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex justify-between items-center">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Manuscript Details
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    {selectedManuscriptIndex !== null
                      ? `${selectedManuscriptIndex + 1} of ${manuscripts.length}`
                      : selectedManuscript?.revision_id || selectedManuscript?.id}
                  </p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <div className="space-y-6">
                {/* Title */}
                <div className="bg-white rounded-lg border border-gray-200 p-6">
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Title</h4>
                  <h2 className="text-xl font-bold text-gray-900">
                    {selectedManuscript.title}
                  </h2>
                </div>

                {/* Status + Type */}
                <div className="flex items-center justify-between">
                  <span
                    className={`inline-flex items-center px-3 py-1 text-sm font-medium rounded-full border ${getStatusClass(
                      selectedManuscript.status
                    )}`}
                  >
                    {getStatusIcon(selectedManuscript.status)}
                    {formatStatus(selectedManuscript.status)}
                  </span>
                  <span className="text-sm text-gray-600">
                    Article Type:{' '}
                    <span className="font-medium">
                      {selectedManuscript.article_type}
                    </span>
                  </span>
                </div>

                {/* Abstract */}
                {selectedManuscript.abstract && (
                  <div className="bg-white rounded-lg border border-gray-200 p-6">
                    <h4 className="text-sm font-medium text-gray-500 mb-4">
                      Abstract
                    </h4>
                    <RichTextViewer content={selectedManuscript.abstract} />
                  </div>
                )}

                {/* Authors */}
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <h4 className="text-sm font-medium text-gray-500 mb-3 flex items-center">
                    <Users className="w-4 h-4 mr-2" />
                    Authors
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedManuscript.authors_list?.map((author, index) => {
                      const isCurrentUser =
                        author.name.includes(user?.firstName || '') &&
                        author.name.includes(user?.lastName || '');
                      return (
                        <span
                          key={index}
                          className={`px-3 py-1 text-sm rounded-full ${isCurrentUser
                              ? 'bg-[--asfi-blue] text-white font-medium'
                              : 'bg-gray-100 text-gray-700'
                            }`}
                        >
                          {author.name}
                          {isCurrentUser && ' (You)'}
                        </span>
                      );
                    })}
                  </div>
                </div>

                {/* Keywords */}
                {selectedManuscript.keywords &&
                  selectedManuscript.keywords.length > 0 && (
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <h4 className="text-sm font-medium text-gray-500 mb-3 flex items-center">
                        <Hash className="w-4 h-4 mr-2" />
                        Keywords
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedManuscript.keywords.map((keyword, index) => (
                          <span
                            key={index}
                            className="px-3 py-1 bg-purple-50 text-purple-700 text-sm rounded-full"
                          >
                            {keyword}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                {/* Dates */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h4 className="text-sm font-medium text-gray-500 mb-1">
                      Submitted Date
                    </h4>
                    <p className="text-sm text-gray-900 flex items-center">
                      <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                      {formatDate(
                        selectedManuscript.date_submitted ||
                        selectedManuscript.process_start_date
                      )}
                    </p>
                  </div>
                  <div className="bg-white rounded-lg border border-gray-200 p-4">
                    <h4 className="text-sm font-medium text-gray-500 mb-1">
                      Last Updated
                    </h4>
                    <p className="text-sm text-gray-900 flex items-center">
                      <Clock className="w-4 h-4 text-gray-400 mr-2" />
                      {formatDate(
                        selectedManuscript.last_updated ||
                        selectedManuscript.date_submitted
                      )}
                    </p>
                  </div>
                </div>
                {/* Files */}
                {(() => {
                  const files = getManuscriptFiles(selectedManuscript);

                  return (
                    <div className="bg-white rounded-lg border border-gray-200 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-gray-500 flex items-center">
                          <FileIcon className="w-4 h-4 mr-2" />
                          Manuscript Files
                        </h4>
                        <span className="text-xs text-gray-400">
                          {files.length} file{files.length !== 1 ? 's' : ''}
                        </span>
                      </div>

                      {files.length === 0 ? (
                        <p className="text-sm text-gray-500 text-center py-4">
                          No files available
                        </p>
                      ) : (
                        <ul className="divide-y divide-gray-100">
                          {files.map((file) => {
                            const Icon = getFileIcon(file.url);
                            return (
                              <li
                                key={file.key}
                                className="flex items-center justify-between gap-3 py-2.5"
                              >
                                <div className="flex items-center min-w-0 flex-1">
                                  <Icon className="w-4 h-4 text-gray-400 mr-3 flex-shrink-0" />
                                  <div className="min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {file.label}
                                    </p>
                                    <p
                                      className="text-xs text-gray-500 truncate"
                                      title={displayFilename(file.url)}
                                    >
                                      {displayFilename(file.url)}
                                    </p>
                                  </div>
                                </div>

                                <div className="flex items-center gap-2 flex-shrink-0">
                                  <a
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-xs font-medium text-[--asfi-purple] hover:text-[--asfi-blue] hover:underline"
                                  >
                                    Open
                                  </a>
                                  <button
                                    type="button"
                                    onClick={() => handleDownloadFile(file.url, file.label)}
                                    className="p-1.5 text-gray-500 hover:text-[--asfi-purple] hover:bg-purple-50 rounded-md transition-colors"
                                    title={`Download ${file.label}`}
                                  >
                                    <Download className="w-4 h-4" />
                                  </button>
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  );
                })()}
                {/* Related Submissions */}
                <div className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-500 flex items-center">
                      <Clock className="w-4 h-4 mr-2" />
                      Related Submissions
                    </h4>
                    <span className="text-xs text-gray-400">
                      {relatedSubmissions.length} version
                      {relatedSubmissions.length !== 1 ? 's' : ''}
                    </span>
                  </div>

                  {loadingRelated ? (
                    <div className="flex justify-center py-6">
                      <Loader className="w-6 h-6 animate-spin text-purple-600" />
                    </div>
                  ) : relatedSubmissions.length === 0 ? (
                    <div className="text-center py-6">
                      <p className="text-sm text-gray-500">
                        No related submissions found
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Manuscript ID
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Date
                            </th>
                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Status
                            </th>
                            <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200">
                          {relatedSubmissions.map((related, index) => {
                            const relatedId = related.revision_id || related.id;
                            const isCurrent =
                              relatedId ===
                              (selectedManuscript.revision_id ||
                                selectedManuscript.id);

                            return (
                              <tr
                                key={relatedId || index}
                                className={`transition-colors ${isCurrent ? 'bg-green-50' : 'hover:bg-gray-50'
                                  }`}
                              >
                                <td className="px-4 py-3 whitespace-nowrap">
                                  <button
                                    onClick={() =>
                                      handleSelectRelatedManuscript(related)
                                    }
                                    className="text-sm font-medium text-[--asfi-purple] hover:text-[--asfi-blue] hover:underline"
                                    title="View this version"
                                  >
                                    {relatedId}
                                  </button>
                                  {isCurrent && (
                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      Current
                                    </span>
                                  )}
                                </td>

                                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                  {formatDate(
                                    related.date_submitted ||
                                    related.process_start_date
                                  )}
                                </td>

                                <td className="px-4 py-3 whitespace-nowrap">
                                  <span
                                    className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getStatusClass(
                                      related.status
                                    )}`}
                                  >
                                    {getStatusIcon(related.status)}
                                    {formatStatus(related.status)}
                                  </span>
                                  <StatusBadge status={related.status} />
                                </td>

                                <td className="px-4 py-3 whitespace-nowrap text-right">
                                  <div className="flex justify-end">
                                    <ManuscriptActions
                                      submission={related}
                                      navigate={navigate}
                                      onContinue={handleContinueSubmission}
                                      onNavigate={handleCloseModal}
                                      size="sm"
                                      variant="buttons"
                                      emptyFallback={
                                        <span className="text-xs text-gray-400">
                                          —
                                        </span>
                                      }
                                    />
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-gray-200 flex-shrink-0 bg-gray-50">
              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Close
                </button>

                <ManuscriptActions
                  submission={selectedManuscript}
                  navigate={navigate}
                  onContinue={handleContinueSubmission}
                  onNavigate={handleCloseModal}
                  size="md"
                  variant="buttons"
                  emptyFallback={null}
                />
              </div>
            </div>

            {/* Navigation Arrows */}
            <button
              onClick={() => handleNavigateManuscript(-1)}
              disabled={
                selectedManuscriptIndex === null ||
                selectedManuscriptIndex === 0
              }
              className={`absolute left-0 top-1/2 transform -translate-y-1/2 -translate-x-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center ${selectedManuscriptIndex === null || selectedManuscriptIndex === 0
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-gray-50 cursor-pointer'
                }`}
              title="Previous Manuscript"
            >
              <ArrowLeft className="w-5 h-5 text-gray-700" />
            </button>

            <button
              onClick={() => handleNavigateManuscript(1)}
              disabled={
                selectedManuscriptIndex === null ||
                selectedManuscriptIndex === manuscripts.length - 1
              }
              className={`absolute right-0 top-1/2 transform -translate-y-1/2 translate-x-1/2 w-10 h-10 bg-white rounded-full shadow-lg flex items-center justify-center ${selectedManuscriptIndex === null ||
                  selectedManuscriptIndex === manuscripts.length - 1
                  ? 'opacity-50 cursor-not-allowed'
                  : 'hover:bg-gray-50 cursor-pointer'
                }`}
              title="Next Manuscript"
            >
              <ArrowRight className="w-5 h-5 text-gray-700" />
            </button>
          </div>
        </div>
      )}

      {/* Quill styles */}
      <style jsx>{`
        .ql-editor {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen,
            Ubuntu, Cantarell, sans-serif;
          line-height: 1.6;
        }
        .ql-editor h1 { font-size: 2em; margin: 0.67em 0; }
        .ql-editor h2 { font-size: 1.5em; margin: 0.75em 0; }
        .ql-editor h3 { font-size: 1.17em; margin: 0.83em 0; }
        .ql-editor p { margin: 0 0 1em 0; }
        .ql-editor ul, .ql-editor ol { padding-left: 1.5em; margin: 0.5em 0; }
        .ql-editor li { margin: 0.25em 0; }
        .ql-editor a { color: #7e22ce; text-decoration: underline; }
        .ql-editor strong { font-weight: 600; }
        .ql-editor em { font-style: italic; }
        .ql-editor u { text-decoration: underline; }
      `}</style>
    </div>
  );
};

export default ManuscriptsList;